'use client';

import { FetchCurrentOrderResponseBody } from 'bff';
import { IconChevronRight } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'i11n';
import Link from 'next/link';
import { OrderProgressAudience } from '../constants/order-progress';
import { OrderProgressStepper } from './OrderProgressStepper';
import { OrderStatusBadge } from './OrderStatusBadge';

export const buildClaimCurrentOrderQueryKey = (claimId: string) => [
  `claim-current-order-${claimId}`,
];

/**
 * Summary of the claim's current order (the most recent one). Purely
 * presentational plus its own query: how to reach the API and where an order
 * detail lives (if anywhere) are injected by each app, like OrderProgressHeader.
 */
export const ClaimCurrentOrderCard = ({
  claimId,
  fetchCurrentOrder,
  audience,
  buildOrderPath,
}: {
  claimId: string;
  fetchCurrentOrder: () => Promise<FetchCurrentOrderResponseBody>;
  audience: OrderProgressAudience;
  // Present: "Ver orden" link + linked stepper nodes. Absent: tracking-only.
  buildOrderPath?: (orderId: string) => string;
}) => {
  const t = useTranslations('claimCurrentOrder');

  const query = useQuery({
    queryKey: buildClaimCurrentOrderQueryKey(claimId),
    queryFn: fetchCurrentOrder,
  });

  const order = query.data?.order;
  const progress = query.data?.progress;

  if (!order || !progress) return null;

  const basePath = buildOrderPath?.(order.id);

  return (
    <div className='flex w-full flex-col gap-4 rounded-3xl border bg-white p-4'>
      <div className='flex flex-col justify-between gap-2 lg:flex-row lg:items-center'>
        <div className='flex flex-wrap items-center gap-2'>
          <h2 className='text-base font-semibold'>{t('title')}</h2>
          <span className='text-sm text-muted-foreground'>
            {order.ref_id} — {order.provider.legal_name}
          </span>
          <OrderStatusBadge status={progress.order_status} />
        </div>

        {basePath && (
          <Link
            href={basePath}
            className='flex flex-row items-center gap-1 text-sm font-medium text-primary hover:underline'
          >
            {t('viewOrder')}
            <IconChevronRight className='size-4' />
          </Link>
        )}
      </div>

      <div className='py-2'>
        <OrderProgressStepper
          stages={progress.stages}
          audience={audience}
          basePath={basePath}
          activeStage={progress.next_action?.stage ?? null}
          workProof={progress.work_proof}
          scheduledFor={progress.scheduled_for}
        />
      </div>
    </div>
  );
};
